import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import axios from 'axios';
import { sendReport } from '../../functions/sendReport';
import { toSentenceCase } from '../../utils/sentenceCase';
import { setBackButtonUrl } from '../uiSlice';
import '../avatar/avatar.css';
import { FRAME_MASK_STYLE } from '../avatar/ornaments';
import './photobooth.css';

// Must match the same keys Avatar.jsx (step 1) writes before navigating here.
const CATEGORY_STORAGE_KEY = 'pb_category';
const SKIP_PHOTO_STORAGE_KEY = 'pb_skip_photo';
const GENDER_STORAGE_KEY = 'pb_gender';
const SUPERHERO_CATEGORY = 'superheros';

const API = process.env.REACT_APP_PHOTOBOOTH_API_URL;
const S3  = process.env.REACT_APP_S3_PATH            || '';
// /faceswap is slow (ML model load + inference, ~2 min on a cold start) and
// routed through a Lambda Function URL instead of the API Gateway `API`
// above, since API Gateway HTTP APIs hard-cap integration timeout at 30s.
const FACESWAP_API = process.env.REACT_APP_PHOTOBOOTH_FACESWAP_URL || API;

const parseObject = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const extractReportId = (res) => {
  if (!res || typeof res !== 'object') return null;
  return res.reportId || res.id || res?.data?.reportId || res?.data?.id || null;
};

/**
 * Step 2 of the Navratri photobooth game, matching navrand-old/main.php:
 * pick an avatar (or skip to use your own photo) for the day chosen on the
 * previous page, then trigger the face swap.
 */
export default function PhotoboothAvatars() {
  const { user }            = useSelector(state => state.auth);
  const { data: themeData } = useSelector(state => state.theme);
  const navigate            = useNavigate();
  const location            = useLocation();
  const dispatch            = useDispatch();

  // The shared header's Back button reads this from Redux (see dddUI/user.jsx)
  // — this page never set it, so Back fell through to whatever URL (or none)
  // an earlier page had left behind instead of going to step 1.
  useEffect(() => {
    dispatch(setBackButtonUrl('/avatar'));
  }, [dispatch]);

  const [selected,   setSelected]   = useState(null);
  const [processing, setProcessing] = useState(false);
  const [selfieUrl,  setSelfieUrl]  = useState(null);
  const [loadingSelfie, setLoadingSelfie] = useState(true);
  const [noPhoto, setNoPhoto] = useState(false);

  let category = location.state?.category || '';
  if (!category) {
    try {
      category = sessionStorage.getItem(CATEGORY_STORAGE_KEY) || '';
    } catch {
      category = '';
    }
  }

  // "Choose Avatar" on step 1 skips the photo entirely — no selfie/upload
  // exists for this user, so an avatar pick here ships as-is (no face swap)
  // rather than trying to fetch/use a photo that was never taken.
  let skipPhoto = location.state?.skipPhoto ?? null;
  if (skipPhoto === null) {
    try {
      skipPhoto = sessionStorage.getItem(SKIP_PHOTO_STORAGE_KEY) === 'true';
    } catch {
      skipPhoto = false;
    }
  }

  let playerGender = location.state?.gender ?? null;
  if (playerGender === null) {
    try {
      playerGender = sessionStorage.getItem(GENDER_STORAGE_KEY) || '';
    } catch {
      playerGender = '';
    }
  }

  const dayToggles     = parseObject(themeData?.day_toggles);
  const characterTheme = parseObject(themeData?.character_theme);
  const avatarMeta     = parseObject(themeData?.avatar_meta);
  const navratriEnabled = dayToggles.navratri_ai !== false;
  // With "Allow gender" on, only show avatars tagged with the gender picked on
  // step 1 — avatars the admin left as "Both" (no gender) show to everyone.
  const genderFilterOn = dayToggles.allow_gender !== false && (playerGender === 'male' || playerGender === 'female');
  const matchesGender = (file) => {
    const avatarGender = avatarMeta[file]?.gender;
    return !genderFilterOn || !avatarGender || avatarGender === playerGender;
  };
  // "Allow Navratri Ai" off hides the day/navratri avatar set here too —
  // not just the day picker on step 1.
  const dayAvatars = navratriEnabled
    ? (category && Array.isArray(characterTheme[category])
        ? characterTheme[category]
        : (themeData?.character || themeData?.avtar || [])).filter(matchesGender)
    : [];
  // Superhero/extra avatars can be tied to one day by the admin; untagged
  // ones show on every day. With no day picked, nothing is filtered out.
  const matchesDay = (file) => {
    const avatarDay = avatarMeta[file]?.day;
    return !avatarDay || !category || avatarDay === category;
  };
  const superheroAvatars = (Array.isArray(characterTheme[SUPERHERO_CATEGORY]) ? characterTheme[SUPERHERO_CATEGORY] : [])
    .filter(matchesGender)
    .filter(matchesDay);
  const superheroEnabled = dayToggles.superhero_Ai !== false && superheroAvatars.length > 0;
  // Admin-added extra categories — each gets its own tab, shown only once it
  // has at least one avatar (after the gender filter).
  const extraTabs = (Array.isArray(themeData?.extra_categories) ? themeData.extra_categories : [])
    .filter((c) => c && c.key)
    .map((c) => ({
      key: c.key,
      label: c.label || 'More avatars',
      avatars: (Array.isArray(characterTheme[c.key]) ? characterTheme[c.key] : []).filter(matchesGender).filter(matchesDay),
    }))
    .filter((c) => c.avatars.length > 0);

  // Default to whichever set is actually available when only one is enabled.
  const initialTab = !navratriEnabled && superheroEnabled ? 'superhero' : 'day';
  const [activeTab, setActiveTab] = useState(initialTab); // 'day' | 'superhero' | extra key

  const activeExtra = extraTabs.find((c) => c.key === activeTab);
  const avatars = activeExtra
    ? activeExtra.avatars
    : activeTab === 'superhero' ? superheroAvatars : dayAvatars;

  // Switching tabs only changes which grid is browsable — it doesn't clear
  // whatever avatar is already picked (avatar filenames are unique across
  // buckets, so there's no collision risk). Clearing it here used to drop
  // the sharp avatar preview back to the much lower-resolution selfie,
  // which the object-fit:contain box then visibly upscaled/blurred.
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
  };

  const authHeader = () => ({
    Authorization:  `Bearer ${user?.sessionId}&${user?.organizationId}`,
    'Content-Type': 'application/json',
  });

  useEffect(() => {
    if (!user?.sessionId) return;
    if (skipPhoto) {
      // No selfie/upload exists for this user by design — nothing to fetch.
      setLoadingSelfie(false);
      return;
    }
    axios
      .post(`${API}/api/photobooth/selfie`, { userId: user.userId }, { headers: authHeader() })
      .then(res => setSelfieUrl(res.data.imageUrl))
      .catch(() => setNoPhoto(true))
      .finally(() => setLoadingSelfie(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sessionId, skipPhoto]);

  const handleSubmit = async () => {
    if (skipPhoto && !selected) {
      Swal.fire('Avatar required', 'Please choose an avatar first — no photo was taken to swap onto.', 'warning');
      return;
    }

    setProcessing(true);

    Swal.fire({
      title:              'Processing…',
      text:               'Your photo is being created. This may take about 30 seconds.',
      showConfirmButton:  false,
      allowOutsideClick:  false,
      didOpen:            () => Swal.showLoading(),
    });

    try {
      await axios.post(
        `${FACESWAP_API}/api/photobooth/faceswap`,
        {
          userId: user.userId,
          avatarName: selected || '',
          skipFaceswap: skipPhoto,
          // Always empty — no name/text is ever baked onto the final
          // image. The Python service's overlay_name_and_superpower only
          // draws text when manager_name is non-empty (text_overlay.py),
          // so this reliably suppresses it in every scenario.
          managerName: '',
          accentColor: themeData?.button_color || '',
        },
        { headers: authHeader(), timeout: 180_000 },
      );

      // Create the game-server report only once the final image actually
      // exists — mirrors old main.php, which calls addReport() right after
      // setting final_image, not at the earlier upload step.
      try {
        const report = await sendReport({
          role: user.role,
          token: user.token,
          points: 0,
          time: "00:00",
          gameId: user.gameId,
          name: user.name,
          sessionId: user.sessionId,
          userId: user.userId,
          organizationId: user.organizationId,
          ans: "",
        });
        const likesid = extractReportId(report);
        if (likesid) {
          await axios.post(
            `${API}/api/photobooth/likeid`,
            { userId: user.userId, likesid },
            { headers: authHeader() },
          );
        }
      } catch (reportErr) {
        console.error('sendReport (create) failed', reportErr);
      }

      Swal.close();
      navigate('/photobooth/final');
    } catch (err) {
      const msg = err.response?.data?.error || 'Face swap failed. Please try again.';
      Swal.fire({
        icon:  'error',
        title: 'Processing Failed',
        text:  msg,
      }).then(() => navigate('/avatar'));
    } finally {
      setProcessing(false);
    }
  };

  const isMobile = window.innerWidth <= 768;
  const bgImg    = themeData
    ? (isMobile ? themeData.background_mob : themeData.background_desk)
    : null;
  const bgStyle  = bgImg
    ? {
        backgroundImage:      `url("${S3}${bgImg}")`,
        backgroundSize:       '100% 100%',
        backgroundRepeat:     'no-repeat',
        backgroundAttachment: 'fixed',
      }
    : {};

  if (loadingSelfie) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader"></div>
      </div>
    );
  }

  const btnStyle = {
    background: themeData?.button_background
      ? themeData.button_background
      : `linear-gradient(180deg, ${themeData?.button_color || '#fb9905'}, ${themeData?.button_color || '#f97316'})`,
    color: themeData?.button_text_color ? themeData.button_text_color : '#fff',
  };

  const previewSrc = selected ? `${S3}avatar/${selected}` : selfieUrl;
  // Description only shows when the admin's per-category "Description"
  // toggle (Avatar tab) is on for whichever category the selected avatar
  // actually belongs to — not just whenever the avatar happens to have
  // saved description text. Determined by which bucket contains the
  // filename rather than the current tab, since switching tabs doesn't
  // clear the selection (filenames are unique across buckets).
  const avatarDescToggles = parseObject(themeData?.avatar_desc_enabled);
  const selectedCategory = selected
    ? (superheroAvatars.includes(selected)
        ? SUPERHERO_CATEGORY
        : (extraTabs.find((c) => c.avatars.includes(selected))?.key || category))
    : null;
  const descriptionEnabled = selectedCategory ? avatarDescToggles[selectedCategory] === true : false;
  const selectedDescription = descriptionEnabled ? avatarMeta[selected]?.description : '';

  return (
    <div className="av-page pba-page" style={bgStyle}>
      {noPhoto ? (
        <div className="av-wrap">
          <div className="av-card av-closed-card">
            <p style={{ margin: '0 0 16px', textAlign: 'center', fontWeight: 700 }}>
              We couldn't find a photo for you yet.
            </p>
            <div className="av-save-row">
              <button className="av-save-btn" style={btnStyle} onClick={() => navigate('/avatar')}>
                ← Back
              </button>
            </div>
          </div>
        </div>
      ) : (
      <div className="pba-layout">
        {/* ── Left: avatar picker ── */}
        <section className="pba-panel pba-panel-main">
          {themeData?.chooseAvatarHeadingImage ? (
            <img
              src={`${S3}${themeData.chooseAvatarHeadingImage}`}
              alt="Choose your festive avatar"
              className="pba-ribbon pba-ribbon-image"
            />
          ) : (
            <div className="pba-ribbon">Choose your festive avatar</div>
          )}
          <p className="pba-subtitle" style={{ color: themeData?.text_color }}>
            {skipPhoto
              ? 'Select a character for your festive avatar.'
              : 'Select a character, or use your own photo without swapping.'}
          </p>

          <div className="pba-tabs" role="tablist">
            {navratriEnabled && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'day'}
                className={`pba-tab${activeTab === 'day' ? ' pba-tab-active' : ''}`}
                onClick={() => handleTabChange('day')}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="9" cy="8" r="3.2" fill="currentColor" />
                  <circle cx="16.5" cy="9" r="2.6" fill="currentColor" opacity="0.75" />
                  <path d="M3 19c0-3.3 2.7-6 6-6s6 2.7 6 6z" fill="currentColor" />
                  <path d="M14.5 19c0-1.9-.7-3.6-1.9-4.9.9-.6 2-.9 3.9-.9 2.8 0 5 2.2 5 5z" fill="currentColor" opacity="0.75" />
                </svg>
                Navratri avatars
              </button>
            )}
            {superheroEnabled && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'superhero'}
                className={`pba-tab${activeTab === 'superhero' ? ' pba-tab-active' : ''}`}
                onClick={() => handleTabChange('superhero')}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 2.5l2.7 6.1 6.6.6-5 4.4 1.5 6.5L12 16.7l-5.8 3.4 1.5-6.5-5-4.4 6.6-.6z" fill="currentColor" />
                </svg>
                Super avatars
              </button>
            )}
            {extraTabs.map((c) => (
              <button
                key={c.key}
                type="button"
                role="tab"
                aria-selected={activeTab === c.key}
                className={`pba-tab${activeTab === c.key ? ' pba-tab-active' : ''}`}
                onClick={() => handleTabChange(c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="pba-grid-scroll">
            {avatars.length > 0 ? (
              <div className="pba-grid">
                {avatars.map((av, i) => (
                  <button
                    type="button"
                    key={av}
                    aria-pressed={selected === av}
                    className={`pba-tile${selected === av ? ' pba-tile-selected' : ''}`}
                    onClick={() => setSelected(selected === av ? null : av)}
                  >
                    <img src={`${S3}avatar/${av}`} alt={`Avatar ${i + 1}`} loading="lazy" />
                    {selected === av && <span className="pba-tile-check" aria-hidden="true">✓</span>}
                  </button>
                ))}
              </div>
            ) : (
              <p className="pba-empty">
                {activeTab === 'superhero' ? 'No superhero avatars available.' : 'No avatars available for this day.'}
              </p>
            )}
          </div>
        </section>

        {/* ── Right: live preview + actions ── */}
        <section className="pba-panel pba-panel-preview">
          {themeData?.livePreviewHeadingImage ? (
            <img
              src={`${S3}${themeData.livePreviewHeadingImage}`}
              alt="Live preview"
              className="pba-ribbon pba-ribbon-sm pba-ribbon-image"
            />
          ) : (
            <div className="pba-ribbon pba-ribbon-sm">Live preview</div>
          )}

          <div className="pba-preview-stage">
            {previewSrc ? (
              <div className="frame-photo pba-frame-photo">
                <div className="frame-photo-window" style={FRAME_MASK_STYLE}>
                  <img
                    src={previewSrc}
                    alt={selected ? 'Selected avatar' : 'Your uploaded photo'}
                    className="pba-preview-img"
                  />
                </div>
                <img src="/img/frame.png" alt="" className="frame-photo-overlay" />
              </div>
            ) : (
              <div className="pba-preview-empty">Pick an avatar to preview it here.</div>
            )}
          </div>

          {selectedDescription && (
            <div className="av-superpower-box pba-description">
              <div className="av-superpower-label">Did you know</div>
              <div className="av-superpower-text">{toSentenceCase(selectedDescription)}</div>
            </div>
          )}

          <div className="pba-actions">
            <button
              className={`av-save-btn pba-use-btn${themeData?.useAvatarButtonImage && !processing ? ' pba-use-btn-image' : ''}`}
              // Skip the theme's background/text color for the image
              // button — an inline style would otherwise beat the CSS that
              // strips .pba-use-btn-image's background, since inline
              // styles always win over stylesheet rules.
              style={themeData?.useAvatarButtonImage && !processing ? undefined : btnStyle}
              onClick={handleSubmit}
              disabled={processing}
            >
              {themeData?.useAvatarButtonImage && !processing ? (
                <img
                  src={`${S3}${themeData.useAvatarButtonImage}`}
                  alt={selected || skipPhoto ? 'Use this avatar' : 'Use my photo'}
                />
              ) : (
                <>
                  <span className="av-save-btn-orn" aria-hidden="true">✦</span>
                  {processing ? 'Processing…' : (selected || skipPhoto ? 'Use this avatar' : 'Use my photo')}
                  <span className="av-save-btn-orn" aria-hidden="true">✦</span>
                </>
              )}
            </button>
          </div>
        </section>
      </div>
      )}
    </div>
  );
}
